"use client";

import { useCallback, useEffect, useState } from "react";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";
import { toast } from "sonner";
import {
  Anchor,
  Building2,
  MapPin,
  DollarSign,
  Clock,
  Calendar,
  Briefcase,
  AlertTriangle,
  Ship,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { getPublicJobDetail, applyForJob, type JobPost } from "@/lib/api/jobs";
import { useAuthStore } from "@/store/authStore";
import { useProfile } from "@/hooks/useProfile";
import { useMatching } from "@/hooks/useMatching";
import { MatchScoreBreakdown, isSkillsExcluded } from "@/components/matching";
import type { SeafarerProfile, ShoreProfile } from "@/lib/types";
import DetailHeader from "@/components/dashboard/DetailHeader";
import { FullSectionLoading } from "@/components/dashboard/LoadingState";
import GlassPanel from "@/components/dashboard/GlassPanel";
import Button from "@/components/ui/Button";
import Badge from "@/components/ui/Badge";

export default function PublicJobDetailPage() {
  const { id } = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const jobId = Number(id);
  const [job, setJob] = useState<JobPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);

  const { user, isAuthenticated } = useAuthStore();
  const role = user?.role ?? null;
  const isCandidate = role === "seafarer" || role === "shore";
  const { profile } = useProfile();
  const { matchScore, isMatchScoreLoading } = useMatching({ jobId: String(jobId) });

  const canApply = job !== null && (job.isOnboard ? role === "seafarer" : role === "shore");
  const applyIntent = searchParams.get("apply") === "true";

  const candidateSkills =
    isCandidate && profile
      ? (profile as SeafarerProfile | ShoreProfile).skills
      : undefined;
  const skillsExcluded = isSkillsExcluded(job?.requiredSkills, candidateSkills);

  const loadJob = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPublicJobDetail(jobId);
      setJob(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load job details");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId) loadJob();
  }, [jobId, loadJob]);

  const handleApply = async () => {
    if (!isAuthenticated) {
      router.push(`/login?redirect=/jobs/${jobId}?apply=true`);
      return;
    }
    if (!canApply) {
      toast.error(
        job?.isOnboard
          ? "Only seafarer accounts can apply to vessel positions."
          : "Only shore accounts can apply to shore positions."
      );
      return;
    }
    if (!job) return;
    setApplying(true);
    try {
      await applyForJob(job.id);
      setApplied(true);
      toast.success("Application submitted", {
        description: `You have applied for ${job.title}.`,
      });
    } catch (err: unknown) {
      toast.error("Application failed", {
        description: err instanceof Error ? err.message : "Could not submit application",
      });
    } finally {
      setApplying(false);
    }
  };

  // Scroll to the apply CTA when the user lands back from login with apply intent.
  useEffect(() => {
    if (applyIntent && !loading && job && canApply) {
      const cta = document.getElementById('job-apply-cta');
      if (cta) {
        cta.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [applyIntent, loading, job, canApply]);

  if (loading) {
    return <FullSectionLoading label="Loading job details..." />;
  }

  if (error || !job) {
    return (
      <div className="mx-auto max-w-4xl py-20 text-center">
        <AlertTriangle className="mx-auto h-10 w-10 text-danger" />
        <p className="mt-3 text-foreground/80">{error ?? "Job not found"}</p>
        <Button href="/jobs" variant="outline" className="mt-6">
          Back to Browse Jobs
        </Button>
      </div>
    );
  }

  const isOnboard = job.isOnboard;
  const positionLabel = isOnboard ? "Rank" : "Position";
  const positionValue = job.positionDisplay || (isOnboard ? job.rank?.rankName : job.shorePosition?.positionName) || "Not specified";

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <DetailHeader
        title={job.title}
        backHref="/jobs"
        backLabel="Back to jobs"
        subtitle={
          <>
            {isOnboard ? <Anchor className="mr-1 inline h-3.5 w-3.5" /> : <Building2 className="mr-1 inline h-3.5 w-3.5" />}
            {isOnboard ? "Vessel-based" : "Shore-based"} at {job.companyName}
            {" "}&middot;{" "}
            Posted {job.createdAt ? new Date(job.createdAt).toLocaleDateString() : "—"}
          </>
        }
        badge={{
          label: job.status,
          variant: job.status === "published" ? "success" : "warning",
        }}
        actions={
          (!isAuthenticated || canApply) && (
            <Button
              onClick={handleApply}
              loading={applying}
              disabled={applied || applying}
              className="shrink-0"
            >
              {applied ? (
                <>
                  <CheckCircle2 className="h-4 w-4" /> Applied
                </>
              ) : !isAuthenticated ? (
                "Sign in to Apply"
              ) : (
                "Apply Now"
              )}
            </Button>
          )
        }
      />

      {/* Key facts */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { icon: <Briefcase className="h-5 w-5" />, label: positionLabel, value: positionValue },
          { icon: <DollarSign className="h-5 w-5" />, label: "Salary", value: job.salaryDisplay || (job.salaryMinUsd ? `$${job.salaryMinUsd.toLocaleString()}` : "Not specified") },
          { icon: <MapPin className="h-5 w-5" />, label: "Joining Port", value: job.joiningPort || "Not specified" },
          { icon: <Calendar className="h-5 w-5" />, label: "Apply By", value: job.applyByDate ? new Date(job.applyByDate).toLocaleDateString() : "—" },
        ].map((stat) => (
          <GlassPanel key={stat.label} className="p-4 text-center">
            <div className="mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-card text-muted-foreground">
              {stat.icon}
            </div>
            <p className="text-sm font-semibold text-foreground">{stat.value}</p>
            <p className="text-xs text-muted-foreground">{stat.label}</p>
          </GlassPanel>
        ))}
      </div>

      {/* Match score breakdown */}
      {isCandidate && (
        <>
          {isMatchScoreLoading ? (
            <GlassPanel className="flex items-center justify-center gap-2 p-6 text-sm text-ash">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading match breakdown...
            </GlassPanel>
          ) : matchScore ? (
            <MatchScoreBreakdown score={matchScore} skillsExcluded={skillsExcluded} />
          ) : null}
        </>
      )}

      {/* Company + metadata */}
      <GlassPanel className="p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-card">
            {job.companyLogoUrl ? (
              <Image
                src={job.companyLogoUrl}
                alt={job.companyName}
                width={56}
                height={56}
                className="h-full w-full rounded-xl object-cover"
              />
            ) : (
              <Ship className="h-7 w-7 text-neon-amber" />
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-foreground">{job.companyName}</h2>
            <p className="text-sm text-ash">Posted by {job.postedByName || "the hiring team"}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Badge variant="info">{job.contractType}</Badge>
              {job.vesselType && <Badge variant="default">{job.vesselType.vesselTypeName}</Badge>}
              {job.matchScore !== undefined && job.matchScore > 0 && (
                <Badge variant={job.matchScore >= 80 ? "success" : job.matchScore >= 50 ? "warning" : "default"}>
                  {job.matchScore}% Match
                </Badge>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted">
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                Posted {new Date(job.createdAt).toLocaleDateString()}
              </span>
              {job.rotationSchedule && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {job.rotationSchedule}
                </span>
              )}
            </div>
          </div>
        </div>
      </GlassPanel>

      {/* Description / requirements */}
      <GlassPanel className="p-6">
        <h2 className="mb-4 text-lg font-semibold text-foreground">Job Description</h2>
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">
          {job.description || "The employer hasn't added a description for this job yet."}
        </p>

        {job.responsibilities && (
          <>
            <h3 className="mb-2 mt-6 text-sm font-semibold text-foreground">Responsibilities</h3>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">{job.responsibilities}</p>
          </>
        )}
        {job.requirements && (
          <>
            <h3 className="mb-2 mt-6 text-sm font-semibold text-foreground">Requirements</h3>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">{job.requirements}</p>
          </>
        )}
        {job.benefits && (
          <>
            <h3 className="mb-2 mt-6 text-sm font-semibold text-foreground">Benefits</h3>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">{job.benefits}</p>
          </>
        )}
      </GlassPanel>

      {/* Required certificates & documents */}
      {(job.requiredCertificates.length > 0 || job.requiredDocuments.length > 0) && (
        <GlassPanel className="p-6">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Required Documents & Certificates</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {job.requiredCertificates.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-foreground">Certificates</h3>
                <ul className="list-inside list-disc space-y-1 text-sm text-ash">
                  {job.requiredCertificates.map((cert) => (
                    <li key={cert.id}>{cert.certName}</li>
                  ))}
                </ul>
              </div>
            )}
            {job.requiredDocuments.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-foreground">Documents</h3>
                <ul className="list-inside list-disc space-y-1 text-sm text-ash">
                  {job.requiredDocuments.map((doc, idx) => (
                    <li key={idx}>{doc}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </GlassPanel>
      )}

      {/* Apply CTA */}
      {(!isAuthenticated || canApply) && (
        <GlassPanel id="job-apply-cta" variant="glow" className="flex flex-col items-center justify-between gap-4 p-6 sm:flex-row">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Interested in this position?</h3>
            <p className="text-sm text-ash">
              {!isAuthenticated
                ? "Sign in to apply and the employer will review your profile."
                : "Apply now and the employer will review your profile."}
            </p>
          </div>
          <Button
            size="lg"
            onClick={handleApply}
            loading={applying}
            disabled={applied || applying}
            className="shrink-0"
          >
            {applied ? (
              <>
                <CheckCircle2 className="h-4 w-4" /> Applied
              </>
            ) : !isAuthenticated ? (
              "Sign in to Apply"
            ) : (
              "Apply Now"
            )}
          </Button>
        </GlassPanel>
      )}

      {isAuthenticated && !canApply && role !== null && (
        <GlassPanel className="p-6 text-center">
          <p className="text-sm text-ash">
            {role === "employer"
              ? "Employer accounts cannot apply to job postings."
              : job?.isOnboard
                ? "Only seafarer accounts can apply to vessel positions."
                : "Only shore accounts can apply to shore positions."}
          </p>
        </GlassPanel>
      )}
    </div>
  );
}
